/* テーマエディタの section 再描画で script が再評価されても、トップレベル宣言を global に残さない */
(() => {
  if (customElements.get('aureme-faq')) return;

  const FAQ_SELECTORS = Object.freeze({
    empty: '[data-faq-empty]',
    filter: '[data-faq-filter]',
    group: '[data-faq-group]',
    item: '[data-faq-item]',
    answer: '.aureme-faq-page__answer',
  });

  const FAQ_TARGET_TYPES = Object.freeze({
    empty: 'empty',
    category: 'category',
    unknown: 'unknown',
  });

  const FAQ_HISTORY_MODES = Object.freeze({
    push: 'push',
    replace: 'replace',
  });

  const PAGE_TOP_SCROLL_OPTIONS = Object.freeze({ top: 0, left: 0, behavior: 'auto' });

  /*
   * URL hash とカテゴリ DOM の対応だけを扱う。
   * 初期 hash は bootstrap 側が一度預かるため、消費後に pending 表示を解除する責務もここへ寄せる。
   */
  class FaqHashController {
    constructor(groups) {
      this.groupById = new Map(groups.map((group) => [group.id, group]));
    }

    normalizeHash(hash) {
      return hash.startsWith('#') ? hash.slice(1) : hash;
    }

    formatHash(hash) {
      if (!hash) return '';
      return hash.startsWith('#') ? hash : `#${hash}`;
    }

    resolveTarget(hash) {
      const normalizedHash = this.normalizeHash(hash);

      if (!normalizedHash) {
        return { type: FAQ_TARGET_TYPES.empty, category: '' };
      }

      const group = this.groupById.get(normalizedHash);
      if (group) {
        return {
          type: FAQ_TARGET_TYPES.category,
          group,
          category: group.dataset.faqCategory || '',
        };
      }

      return { type: FAQ_TARGET_TYPES.unknown, category: '' };
    }

    updateHash(hash, mode = FAQ_HISTORY_MODES.push) {
      const nextHash = this.formatHash(hash);
      if (nextHash === window.location.hash) return;

      const nextUrl = `${window.location.pathname}${window.location.search}${nextHash}`;
      const historyMethod = mode === FAQ_HISTORY_MODES.replace ? 'replaceState' : 'pushState';
      window.history[historyMethod](window.history.state, '', nextUrl);
    }

    clearHash() {
      this.updateHash('', FAQ_HISTORY_MODES.replace);
    }

    consumeInitialHash() {
      const bootstrap = window.AuremeFaqBootstrap;
      const hash = bootstrap?.initialHash || '';
      if (bootstrap) {
        bootstrap.initialHash = '';
      }
      return hash;
    }

    releaseInitialHashPendingState() {
      const bootstrap = window.AuremeFaqBootstrap;
      if (typeof bootstrap?.releasePendingState === 'function') {
        bootstrap.releasePendingState();
        return;
      }

      document.documentElement.removeAttribute('data-aureme-faq-hash-pending');
    }
  }

  /*
   * カテゴリ切替・accordion 開閉後の高さ変化を待つ scroll を一元管理し、
   * section unload 時に未実行の requestAnimationFrame を確実に捨てる。
   */
  class FaqFrameScheduler {
    constructor() {
      this.frames = new Map();
    }

    request(name, callback) {
      this.cancel(name);
      const frameId = requestAnimationFrame(() => {
        this.frames.delete(name);
        callback();
      });
      this.frames.set(name, frameId);
    }

    requestAfterFrames(name, frameCount, callback) {
      this.cancel(name);

      const requestNextFrame = (remainingFrames) => {
        const frameId = requestAnimationFrame(() => {
          if (remainingFrames <= 1) {
            this.frames.delete(name);
            callback();
            return;
          }

          requestNextFrame(remainingFrames - 1);
        });
        this.frames.set(name, frameId);
      };

      requestNextFrame(frameCount);
    }

    cancel(name) {
      const frameId = this.frames.get(name);
      if (frameId === undefined) return;

      cancelAnimationFrame(frameId);
      this.frames.delete(name);
    }

    cancelAll() {
      for (const frameId of this.frames.values()) {
        cancelAnimationFrame(frameId);
      }
      this.frames.clear();
    }
  }

  /*
   * details の native toggle を使いながら、カテゴリ内の単一展開と answer の inert 同期だけを担当する。
   * JS から開いた item は toggle handler 側のスクロールを抑制し、呼び出し元で位置調整する。
   */
  class FaqAccordionController {
    constructor(items, { onItemOpen } = {}) {
      this.items = items;
      this.onItemOpen = onItemOpen;
      this.suppressedToggleItems = new WeakSet();
      this.handleItemToggle = this.handleItemToggle.bind(this);
    }

    connect() {
      for (const item of this.items) {
        this.updateItemAnswerState(item);
        item.addEventListener('toggle', this.handleItemToggle);
      }
    }

    disconnect() {
      for (const item of this.items) {
        item.removeEventListener('toggle', this.handleItemToggle);
      }
    }

    handleItemToggle(event) {
      const item = event.currentTarget;
      this.updateItemAnswerState(item);

      if (!item.open) return;

      if (this.suppressedToggleItems.has(item)) {
        this.suppressedToggleItems.delete(item);
        return;
      }

      this.closeSiblingItems(item);
      this.onItemOpen?.(item);
    }

    openItem(item, { scroll = true } = {}) {
      if (!item) return;

      if (!item.open) {
        this.suppressedToggleItems.add(item);
      }

      this.setItemOpen(item, true);
      this.closeSiblingItems(item);

      if (scroll) {
        this.onItemOpen?.(item);
      }
    }

    closeSiblingItems(openedItem) {
      const parentGroup = openedItem.closest(FAQ_SELECTORS.group);
      if (!parentGroup) return;

      this.closeItems(parentGroup.querySelectorAll(FAQ_SELECTORS.item), { except: openedItem });
    }

    closeAllItems() {
      this.closeItems(this.items);
    }

    closeItemsInGroup(group) {
      this.closeItems(group.querySelectorAll(FAQ_SELECTORS.item));
    }

    updateItemAnswerState(item) {
      const answer = item.querySelector(FAQ_SELECTORS.answer);
      if (!answer) return;
      answer.inert = !item.open;
    }

    setItemOpen(item, open) {
      item.open = open;
      this.updateItemAnswerState(item);
    }

    closeItems(items, { except = null } = {}) {
      for (const item of items) {
        if (item === except) continue;
        this.setItemOpen(item, false);
      }
    }
  }

  /*
   * フィルタ・hash・テーマエディタ block select を統合する custom element。
   * 表示カテゴリの決定はここに集約し、accordion と scroll の細部は専用 controller に委譲する。
   */
  class AuremeFaq extends HTMLElement {
    constructor() {
      super();
      this.alignOpenItemIntoView = this.alignOpenItemIntoView.bind(this);
      this.handleFilterClick = this.handleFilterClick.bind(this);
      this.handleHashChange = this.handleHashChange.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
    }

    connectedCallback() {
      this.emptyElement = this.querySelector(FAQ_SELECTORS.empty);
      this.filterButtons = Array.from(this.querySelectorAll(FAQ_SELECTORS.filter));
      this.groups = Array.from(this.querySelectorAll(FAQ_SELECTORS.group));
      this.items = Array.from(this.querySelectorAll(FAQ_SELECTORS.item));
      this.activeCategory = '';
      this.hashController = new FaqHashController(this.groups);
      this.frames = new FaqFrameScheduler();
      this.accordion = new FaqAccordionController(this.items, {
        onItemOpen: this.alignOpenItemIntoView,
      });

      for (const button of this.filterButtons) {
        button.addEventListener('click', this.handleFilterClick);
      }

      this.accordion.connect();

      window.addEventListener('hashchange', this.handleHashChange);
      window.addEventListener('popstate', this.handleHashChange);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);

      const initialHash = this.hashController.consumeInitialHash();
      if (initialHash) {
        this.syncToHash(initialHash, {
          scroll: true,
          scrollBehavior: 'auto',
          historyMode: FAQ_HISTORY_MODES.replace,
          onSettled: () => this.scheduleInitialHashPendingRelease(),
        });
        return;
      }

      this.hashController.releaseInitialHashPendingState();
      this.syncToHash(window.location.hash, { scroll: Boolean(window.location.hash) });
    }

    disconnectedCallback() {
      for (const button of this.filterButtons) {
        button.removeEventListener('click', this.handleFilterClick);
      }

      this.accordion.disconnect();

      window.removeEventListener('hashchange', this.handleHashChange);
      window.removeEventListener('popstate', this.handleHashChange);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);

      this.hashController.releaseInitialHashPendingState();
      this.frames.cancelAll();
    }

    handleFilterClick(event) {
      const targetButton = event.currentTarget;
      const targetCategory = targetButton.dataset.faqFilter || '';
      const targetHash = targetButton.getAttribute('href') || '';

      if (!targetCategory || !targetHash) return;

      event.preventDefault();

      if (targetCategory === this.activeCategory) {
        this.selectCategory('', { clearHash: true, closeItems: true });
        return;
      }

      this.selectCategory(targetCategory, {
        closeItems: true,
        hash: targetHash,
        scroll: true,
      });
    }

    handleHashChange() {
      this.syncToHash(window.location.hash, { scroll: true });
    }

    handleBlockSelect(event) {
      if (!(event.target instanceof Element)) return;

      const selectedBlock = event.target.closest(`${FAQ_SELECTORS.item}, ${FAQ_SELECTORS.group}`);
      if (!selectedBlock || !this.contains(selectedBlock)) return;

      const category = selectedBlock.dataset.faqCategory || '';
      this.selectCategory(category, { closeItems: false });

      if (selectedBlock.matches(FAQ_SELECTORS.item)) {
        this.accordion.openItem(selectedBlock);
        return;
      }
    }

    syncToHash(hash, { scroll = true, scrollBehavior, historyMode, onSettled } = {}) {
      const target = this.hashController.resolveTarget(hash);

      if (target.type === FAQ_TARGET_TYPES.category) {
        this.syncToCategoryHash(hash, target.category, {
          scroll,
          scrollBehavior,
          historyMode,
          onSettled,
        });
        return;
      }

      this.syncToDefaultHash(target, onSettled);
    }

    syncToCategoryHash(hash, category, { scroll, scrollBehavior, historyMode, onSettled }) {
      if (historyMode) {
        this.hashController.updateHash(hash, historyMode);
      }

      this.selectCategory(category, { closeItems: true });

      if (!scroll) {
        onSettled?.();
        return;
      }

      const isScrollScheduled = this.scrollToHashTarget(hash, {
        behavior: scrollBehavior ?? this.getScrollBehavior(),
        onDone: onSettled,
      });
      if (!isScrollScheduled) onSettled?.();
    }

    syncToDefaultHash(target, onSettled) {
      const isUnknownHash = target.type === FAQ_TARGET_TYPES.unknown;

      if (isUnknownHash) {
        this.hashController.clearHash();
      }

      this.selectCategory('', { closeItems: true });

      if (isUnknownHash) {
        this.scrollPageToTop();
      }

      onSettled?.();
    }

    scrollPageToTop() {
      window.scrollTo(PAGE_TOP_SCROLL_OPTIONS);

      // 直接アクセス時のブラウザ標準スクロールが後から走る場合に備えて、描画後にも先頭へ戻す
      this.frames.request('pageTopScroll', () => {
        window.scrollTo(PAGE_TOP_SCROLL_OPTIONS);
      });
    }

    scrollToHashTarget(hash, { behavior = this.getScrollBehavior(), onDone } = {}) {
      const target = this.hashController.resolveTarget(hash);
      if (!target.group || target.group.hidden) return false;
      const targetGroup = target.group;

      this.frames.request('scroll', () => {
        if (!this.isConnected || !targetGroup.isConnected || targetGroup.hidden) {
          onDone?.();
          return;
        }

        this.scrollElementToStart(targetGroup, behavior);
        onDone?.();
      });

      return true;
    }

    scheduleInitialHashPendingRelease() {
      this.frames.request('releasePending', () => {
        this.hashController.releaseInitialHashPendingState();
      });
    }

    alignOpenItemIntoView(item) {
      if (!item?.open) return;

      // 他の質問を閉じた後の高さ変化を待ってから位置を合わせる
      this.frames.requestAfterFrames('itemScroll', 2, () => {
        if (!this.isConnected || !item.isConnected || !item.open) return;

        this.scrollElementToStart(item);
      });
    }

    scrollElementToStart(element, behavior = this.getScrollBehavior()) {
      element.scrollIntoView({ block: 'start', behavior });
    }

    selectCategory(
      category,
      {
        clearHash = false,
        closeItems = false,
        hash = '',
        historyMode = FAQ_HISTORY_MODES.push,
        scroll = false,
      } = {},
    ) {
      this.applyFilter(category);

      if (closeItems) {
        this.accordion.closeAllItems();
      }

      // hash 指定の遷移を優先し、選択解除の経路だけ clearHash を使う
      if (hash) {
        this.hashController.updateHash(hash, historyMode);
      } else if (clearHash) {
        this.hashController.clearHash();
      }

      if (scroll && hash) {
        this.scrollToHashTarget(hash);
      }
    }

    getScrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    applyFilter(category) {
      this.activeCategory = category;

      let hasVisibleGroup = false;
      for (const group of this.groups) {
        const groupCategory = group.dataset.faqCategory || '';
        const isVisible = !category || groupCategory === category;

        group.hidden = !isVisible;
        if (!isVisible) {
          this.accordion.closeItemsInGroup(group);
          continue;
        }

        hasVisibleGroup = true;
      }

      for (const button of this.filterButtons) {
        this.updateFilterButtonState(button, button.dataset.faqFilter === category);
      }

      this.updateEmptyState(!hasVisibleGroup);
    }

    updateFilterButtonState(button, isActive) {
      button.classList.toggle('is-active', isActive);
      if (isActive) {
        button.setAttribute('aria-current', 'location');
        return;
      }

      button.removeAttribute('aria-current');
    }

    updateEmptyState(isEmpty) {
      if (!this.emptyElement) return;
      this.emptyElement.hidden = !isEmpty;
    }
  }

  customElements.define('aureme-faq', AuremeFaq);
})();
